import { TFunction } from 'i18next';

import { fieldTypeToValidation } from '@breathelife/insurance-form-builder';
import { AutocompleteOption } from '@breathelife/ui-components';
import {
  DataLabel,
  DataLabels,
  FieldTypes,
  InfoVariants,
  IsoCountryCode,
  NumericalDataTypeEnum,
} from '@zinnia/form-engine-sdk';

export function getFieldTypeOptions(t: TFunction): AutocompleteOption[] {
  return Object.values(FieldTypes)
    .map((fieldType) => ({
      value: fieldType,
      label: t(`admin.questionnaireManagement.fieldTypes.${fieldType}`),
    }))
    .filter(({ value }) => value !== FieldTypes.yearMonth)
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
}

export const DATA_LABEL_INHERIT_KEY = 'inherit';

export function getDataLabelOptions(t: TFunction): AutocompleteOption[] {
  return [
    DATA_LABEL_INHERIT_KEY,
    DataLabels.Unknown,
    DataLabels.KnownSafe,
    DataLabels.PII,
    DataLabels.PHI,
    DataLabels.IdentifiablePHI,
    DataLabels.ReferenceNumber,
    DataLabels.PostalCode,
    DataLabels.DateOfBirth,
    DataLabels.IdentifiableDate,
    DataLabels.NonIdentifiableDate,
    DataLabels.FreeFormText,
    DataLabels.AgeAtServiceTime,
    DataLabels.Language,
    DataLabels.AdvisorEmail,
    DataLabels.LoginEmail,
    DataLabels.InsuredPersonEmail,
    DataLabels.LeadEmail,
    DataLabels.Email,
  ].map((dataLabel) => ({
    value: dataLabel,
    label: t(`admin.questionnaireManagement.dataLabels.${dataLabel}`),
  }));
}

export function dataLabelSelectionToDataLabel(selection: string): DataLabel | undefined {
  if (selection === DATA_LABEL_INHERIT_KEY) {
    // Don't set a label, empty labels will inherit from their parents.
    return undefined;
  }

  return selection as DataLabel;
}

export type ValidationOptionsByFieldType = { [key in FieldTypes]: AutocompleteOption[] };

export function getValidationOptionsByFieldType(t: TFunction): ValidationOptionsByFieldType {
  const validationOptions = Object.keys(FieldTypes).reduce((acc, fieldType) => {
    acc[fieldType as FieldTypes] = Object.values(fieldTypeToValidation[fieldType as FieldTypes]).map(
      (validationKey) => ({
        value: validationKey,
        label: t(`admin.questionnaireManagement.validateAs.${validationKey}`),
      }),
    );
    return acc;
  }, {} as ValidationOptionsByFieldType);

  return validationOptions;
}

export function getInfoFieldStylingOptions(t: TFunction): AutocompleteOption[] {
  return Object.keys(InfoVariants)
    .map((styleKey) => ({
      label: t(`admin.questionnaireManagement.infoFieldStyles.${styleKey}`),
      value: styleKey,
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));
}

export function getNumericalDataTypeOptions(t: TFunction): AutocompleteOption[] {
  return Object.keys(NumericalDataTypeEnum)
    .map((numericDataTypeKey) => ({
      label: t(`admin.questionnaireManagement.numericalDataTypeOptions.${numericDataTypeKey}`),
      value: numericDataTypeKey,
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));
}

export function getCountryCodeOptions(t: TFunction): AutocompleteOption[] {
  const countryCodes: IsoCountryCode[] = ['CA', 'US'];
  return countryCodes.map((countryCode) => ({
    value: countryCode,
    label: t(`admin.questionnaireManagement.input.autocompleteCountryCodes.${countryCode}`),
  }));
}
